import { useState } from 'react'
import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'

import { CalendarIcon } from '@gto/icons-react/outline/calendar'
import { PencilIcon } from '@gto/icons-react/outline/pencil'
import { ShieldCheckIcon } from '@gto/icons-react/outline/shield-check'
import { TrophyIcon } from '@gto/icons-react/outline/trophy'
import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import { Typography } from '@gto/ui/typography'

import {
  type AffiliatePaymentMethodRequestSchema,
  type AffiliateSocialLinkSchema,
  getGetMyProfileQueryKey,
  useGetMyProfile,
  useUpdateMyProfile,
  useUpdatePaymentMethod,
} from '@/api/gen'
import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { TextField } from '@/components/form'
import { InlineQueryError } from '@/components/inline-query-error'
import { MutationError } from '@/components/mutation-error'
import { Page } from '@/components/page'
import { PayoutMethodFields } from '@/components/payout-method-fields'
import { PayoutMethodSummary } from '@/components/payout-method-summary'
import { SectionCard } from '@/components/section-card'
import { SocialLinksFields } from '@/components/social-links-fields'
import { SocialLinksSummary } from '@/components/social-links-summary'
import { StatusBadge } from '@/components/status-badge'
import { UsdOnlyConfirmationField } from '@/components/usd-only-confirmation-field'
import { usePayoutMethodForm } from '@/components/use-payout-method-form'
import { useSocialLinksForm } from '@/components/use-social-links-form'
import { isPayoutMethod } from '@/domain/payout-methods'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { extractValidationDetail } from '@/utils/extract-validation-detail'

// --- Profile Section ---

const PROFILE_FIELDS = [
  { key: 'display_name', labelId: 'profile.displayName' },
  { key: 'email', labelId: 'profile.email', type: 'email' },
  { key: 'country', labelId: 'profile.country' },
] as const

// --- Main Page ---

export const SettingsPage = () => {
  const { formatMessage } = useIntl()
  const profileQuery = useGetMyProfile()
  const {
    data: partner,
    isLoading: profileLoading,
    isError: profileError,
    refetch: refetchProfile,
  } = profileQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  useReportQueryError(profileQuery, 'getMyProfile failed: ')

  const memberSince =
    partner?.created_at != null && partner.created_at !== ''
      ? new Date(partner.created_at).toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        })
      : '—'

  return (
    <Page>
      <Page.Header title={<FormattedMessage id="settings.title" />} />

      <Page.Content>
        {profileError ? (
          <InlineQueryError
            data-tst="settings-error"
            messageId="settings.error"
            onRetry={() => void refetchProfile()}
          />
        ) : (
          <>
            <div
              className="flex flex-row flex-wrap gap-2"
              data-tst="kpi-strip"
            >
              <DashboardKpiTile
                icon={<TrophyIcon />}
                label={<FormattedMessage id="profile.tier" />}
                loading={profileLoading}
                tooltip={formatMessage({ id: 'tooltip.profile.tier' })}
                value={
                  <Typography
                    className="font-heading font-semibold leading-100"
                    variant="h5"
                  >
                    {partner?.tier?.name ?? '—'}
                  </Typography>
                }
              />
              <DashboardKpiTile
                icon={<ShieldCheckIcon />}
                label={<FormattedMessage id="profile.status" />}
                loading={profileLoading}
                tooltip={formatMessage({ id: 'tooltip.profile.status' })}
                value={
                  partner?.status != null && partner.status !== '' ? (
                    <StatusBadge status={partner.status} />
                  ) : (
                    <Typography
                      className="font-heading font-semibold leading-100"
                      variant="h5"
                    >
                      —
                    </Typography>
                  )
                }
              />
              <DashboardKpiTile
                icon={<CalendarIcon />}
                label={<FormattedMessage id="profile.memberSince" />}
                loading={profileLoading}
                tooltip={formatMessage({ id: 'tooltip.profile.memberSince' })}
                value={
                  <Typography
                    className="font-heading font-semibold leading-100"
                    variant="h5"
                  >
                    {memberSince}
                  </Typography>
                }
              />
            </div>

            <ProfileSection />
            <PayoutMethodSection />
          </>
        )}
      </Page.Content>
    </Page>
  )
}

// --- Profile Section Component ---

const ProfileSection = () => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const { data: partner } = useGetMyProfile()

  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ display_name: '', email: '', country: '' })

  // The repeatable social-links sub-editor: react-hook-form + zodResolver over the
  // single `socialLinksFormSchema` (SSOT in `@/domain/social-links`). The scalar
  // identity fields above stay a legacy `useState` form (not migrated in this
  // pass); the links editor gates submit so an invalid link blocks the PATCH.
  const {
    form: socialLinksForm,
    reset: resetSocialLinks,
    handleSubmit: handleSocialLinksSubmit,
    applyServerErrors,
  } = useSocialLinksForm()

  // A failed profile update must reach Sentry AND surface inside the form, and
  // must NOT close the editor (success-only side-effect). A 422's actionable
  // `detail` surfaces inline; its per-row `social_links` field errors hang on the
  // matching row via `applyServerErrors`.
  const updateError = useMutationErrorHandler('useUpdateMyProfile failed: ', {
    extractDetail: extractValidationDetail,
  })

  const updateProfile = useUpdateMyProfile({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetMyProfileQueryKey() })
        setEditing(false)
        updateError.clearError()
      },
      // A server 422's per-row field errors hang on the social-link rows; every
      // other failure (or an unmapped error) is a non-field error the inline
      // alert + Sentry own.
      onError: (error) => {
        if (!applyServerErrors(error)) {
          updateError.onError(error)
        }
      },
    },
  })

  const startEditing = () => {
    setEditing(true)
    if (partner) {
      setForm({
        display_name: partner.display_name,
        email: partner.email,
        country: partner.country,
      })
      resetSocialLinks(partner.social_links)
    }
    updateProfile.reset()
    updateError.clearError()
  }

  const cancelEditing = () => {
    setEditing(false)
    if (partner) {
      setForm({
        display_name: partner.display_name,
        email: partner.email,
        country: partner.country,
      })
      resetSocialLinks(partner.social_links)
    }
    updateProfile.reset()
    updateError.clearError()
  }

  // The links resolver validates first; on a valid form it hands over the built
  // wire list and the scalar identity fields go out in the same PATCH body.
  const submitProfile = (social_links: AffiliateSocialLinkSchema[]) => {
    updateError.clearError()
    updateProfile.mutate({
      data: {
        display_name: form.display_name,
        email: form.email,
        country: form.country,
        social_links,
      },
    })
  }

  const setField = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  if (!editing) {
    return (
      <SectionCard>
        <div className="flex items-center justify-between">
          <Typography
            className="font-semibold"
            variant="subtitle1"
          >
            <FormattedMessage id="profile.details" />
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              <PencilIcon
                height={14}
                width={14}
              />
            }
            onClick={startEditing}
          >
            <FormattedMessage id="profile.edit" />
          </Button>
        </div>

        <div className="flex flex-col gap-3">
          {PROFILE_FIELDS.map((field) => (
            <div
              key={field.key}
              className="flex items-baseline justify-between gap-4"
            >
              <Typography
                color="text.secondary"
                variant="body2"
              >
                {formatMessage({ id: field.labelId } as MessageDescriptor)}
              </Typography>
              <Typography
                className="font-medium"
                variant="body2"
              >
                {partner?.[field.key] ?? '—'}
              </Typography>
            </div>
          ))}

          <div className="flex flex-col gap-2">
            <Typography
              color="text.secondary"
              variant="body2"
            >
              <FormattedMessage id="profile.socialLinks" />
            </Typography>
            <SocialLinksSummary links={partner?.social_links} />
          </div>
        </div>
      </SectionCard>
    )
  }

  return (
    <SectionCard onSubmit={handleSocialLinksSubmit(submitProfile)}>
      <Typography
        className="font-semibold"
        variant="subtitle1"
      >
        <FormattedMessage id="profile.details" />
      </Typography>

      <MutationError
        data-tst="profile-update-error"
        detail={updateError.detail}
        messageId="profile.updateError"
        show={updateError.hasError}
      />

      {PROFILE_FIELDS.map((field) => (
        <TextField
          key={field.key}
          label={formatMessage({ id: field.labelId } as MessageDescriptor)}
          type={'type' in field ? field.type : 'text'}
          value={form[field.key]}
          onChange={(e) => setField(field.key, e.target.value)}
        />
      ))}

      <SocialLinksFields
        disabled={updateProfile.isPending}
        form={socialLinksForm}
      />

      {updateProfile.isSuccess && (
        <Alert
          message={<FormattedMessage id="profile.saved" />}
          severity="success"
        />
      )}

      <div className="flex flex-row gap-4">
        <Button
          color="secondary"
          variant="outlined"
          onClick={cancelEditing}
        >
          <FormattedMessage id="admin.common.cancel" />
        </Button>
        <Button
          disabled={updateProfile.isPending}
          type="submit"
          variant="contained"
        >
          <FormattedMessage id="profile.save" />
        </Button>
      </div>
    </SectionCard>
  )
}

// --- Payout Method Section Component ---

const PayoutMethodSection = () => {
  const queryClient = useQueryClient()
  const { data: partner } = useGetMyProfile()

  const savedMethod = isPayoutMethod(partner?.payout_method) ? partner.payout_method : null
  const hasSavedMethod = savedMethod != null && partner?.has_payout_details === true

  const [editing, setEditing] = useState(false)
  const { form, reset, handleSubmit, applyServerErrors } = usePayoutMethodForm({
    requireUsdConfirmation: true,
  })

  // A failed payment-method update must reach Sentry AND surface inside the form,
  // and must NOT close the editor or clear local state. Server 422 field errors
  // are the fallback authority beyond the client validation in the form.
  const updateError = useMutationErrorHandler('useUpdatePaymentMethod failed: ', {
    extractDetail: extractValidationDetail,
  })

  const updatePaymentMethod = useUpdatePaymentMethod({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetMyProfileQueryKey() })
        setEditing(false)
        updateError.clearError()
      },
      // A server 422's field errors hang on the form fields; every other
      // failure is a non-field error the inline alert + Sentry own.
      onError: (error) => {
        if (!applyServerErrors(error)) {
          updateError.onError(error)
        }
      },
    },
  })

  const startEditing = () => {
    setEditing(true)
    reset(savedMethod)
    updatePaymentMethod.reset()
    updateError.clearError()
  }

  const cancelEditing = () => {
    setEditing(false)
    reset(savedMethod)
    updatePaymentMethod.reset()
    updateError.clearError()
  }

  const submitPaymentMethod = (body: AffiliatePaymentMethodRequestSchema) => {
    updateError.clearError()
    updatePaymentMethod.mutate({ data: body })
  }

  if (hasSavedMethod && !editing) {
    return (
      <SectionCard>
        <div className="flex items-center justify-between">
          <Typography
            className="font-semibold"
            variant="subtitle1"
          >
            <FormattedMessage id="settings.payoutMethod" />
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              <PencilIcon
                height={14}
                width={14}
              />
            }
            onClick={startEditing}
          >
            <FormattedMessage id="settings.change" />
          </Button>
        </div>

        <PayoutMethodSummary
          details={partner.payout_details}
          method={savedMethod}
        />
      </SectionCard>
    )
  }

  return (
    <SectionCard onSubmit={handleSubmit(submitPaymentMethod)}>
      <Typography
        className="font-semibold"
        variant="subtitle1"
      >
        <FormattedMessage id="settings.payoutMethod" />
      </Typography>

      <MutationError
        data-tst="payment-method-update-error"
        detail={updateError.detail}
        messageId="settings.updateError"
        show={updateError.hasError}
      />

      <PayoutMethodFields
        disabled={updatePaymentMethod.isPending}
        form={form}
      />

      <UsdOnlyConfirmationField
        disabled={updatePaymentMethod.isPending}
        form={form}
      />

      <Alert
        message={<FormattedMessage id="settings.securityNote" />}
        severity="info"
      />

      {updatePaymentMethod.isSuccess && (
        <Alert
          message={<FormattedMessage id="settings.saved" />}
          severity="success"
        />
      )}

      <div className="flex flex-row gap-4">
        {hasSavedMethod && (
          <Button
            color="secondary"
            variant="outlined"
            onClick={cancelEditing}
          >
            <FormattedMessage id="admin.common.cancel" />
          </Button>
        )}
        <Button
          disabled={updatePaymentMethod.isPending}
          type="submit"
          variant="contained"
        >
          <FormattedMessage id="settings.save" />
        </Button>
      </div>
    </SectionCard>
  )
}
